package fr.manyverse.app.presentation.components.common_tabs

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import fr.manyverse.app.domain.model.DiceFaceStyle
import fr.manyverse.app.domain.model.DiceOutcome
import fr.manyverse.app.domain.model.DieConfig
import fr.manyverse.app.domain.model.outcomeFor
import fr.manyverse.app.presentation.components.dice3d.Dice3DCanvas
import fr.manyverse.app.presentation.dice3d.DiceOneFace
import fr.manyverse.app.presentation.dice3d.rememberDiceImpactHaptics
import fr.manyverse.app.presentation.viewmodel.DiceTrayViewModel
import kotlin.random.Random
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.dice_rolling_message_1
import manyverse.shared.generated.resources.dice_rolling_message_2
import manyverse.shared.generated.resources.dice_rolling_message_3
import manyverse.shared.generated.resources.dice_rolling_message_4
import manyverse.shared.generated.resources.shake_roll_tap_to_close
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

/**
 * Overlay plein écran du lancer par secouage : les dés 3D roulent sur tout l'écran et font vibrer
 * l'appareil à chacun de leurs chocs. Le résultat est celui que donnent les dés en se couchant —
 * rien n'est tiré d'avance — et il est révélé, puis remonté via [onRolled], à l'instant même où
 * ils s'arrêtent. Aucun minuteur ne s'interpose : ce sont les dés qui décident, de bout en bout.
 */
@OptIn(ExperimentalLayoutApi::class)
@Composable
fun DiceShakeOverlay(
    dieConfig: DieConfig,
    count: Int,
    onRolled: (results: List<Int>) -> Unit,
    rollModifier: Int = 0,
    totalEnabled: Boolean = true,
    faceStyle: DiceFaceStyle = DiceFaceStyle.NUMERALS,
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier
) {
    val messages = listOf(
        stringResource(Res.string.dice_rolling_message_1),
        stringResource(Res.string.dice_rolling_message_2),
        stringResource(Res.string.dice_rolling_message_3),
        stringResource(Res.string.dice_rolling_message_4),
    )
    val rollingMessage = remember { messages.random() }
    val seed = remember { Random.nextLong() }
    var results by remember { mutableStateOf<List<Int>?>(null) }

    val trayViewModel: DiceTrayViewModel = koinViewModel()
    val diceHaptics = rememberDiceImpactHaptics()
    val diceColor by trayViewModel.diceColor.collectAsStateWithLifecycle()
    // Le choix de l'utilisateur prime ; le style de face de l'univers n'est que la valeur initiale.
    val storedOneFace by trayViewModel.oneFace.collectAsStateWithLifecycle()
    val oneFace = storedOneFace ?: DiceOneFace.fromFaceStyle(faceStyle)

    // Texte révélé dès que les dés se sont couchés.
    val revealed = results != null

    val currentOnRolled by rememberUpdatedState(onRolled)
    LaunchedEffect(results) {
        results?.let { currentOnRolled(it) }
    }

    Box(
        modifier = modifier
            .fillMaxSize()
            .background(MaterialTheme.colorScheme.scrim.copy(alpha = 0.85f))
            .clickable { onDismiss() }
    ) {
        if (count > 0) {
            // Un halo coloré souligne les dés critiques une fois rangés au centre de l'écran.
            val haloColorFor: (Int) -> Color? = remember(dieConfig) {
                { criticalHaloColor(dieConfig.outcomeFor(it)) }
            }
            Dice3DCanvas(
                dieType = dieConfig.type,
                diceCount = count,
                diceColor = diceColor,
                oneFace = oneFace,
                seed = seed,
                onSettled = { rolled -> if (results == null) results = rolled },
                onImpact = diceHaptics,
                modifier = Modifier
                    .fillMaxSize()
                    .statusBarsPadding(),
                haloColorFor = haloColorFor,
                // Ici les dés roulent sur le voile qui assombrit l'écran, pas sur un feutre :
                // c'est donc lui que leurs faces du dessous doivent renvoyer.
                feltColor = MaterialTheme.colorScheme.scrim,
                wallInset = 24.dp,
                // Sous le libellé « toucher pour fermer », au-dessus des résultats.
                settleTopFraction = 0.10f,
                settleBottomFraction = 0.62f
            )
        }

        Text(
            text = stringResource(Res.string.shake_roll_tap_to_close),
            style = MaterialTheme.typography.labelMedium,
            color = Color.White.copy(alpha = 0.6f),
            textAlign = TextAlign.Center,
            modifier = Modifier
                .align(Alignment.TopCenter)
                .statusBarsPadding()
                .padding(top = 16.dp)
        )

        // Titre + résultats en bande basse : les dés arrêtés occupent la partie haute.
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(16.dp),
            modifier = Modifier
                .align(Alignment.BottomCenter)
                .navigationBarsPadding()
                .padding(bottom = 48.dp)
        ) {
            Text(
                text = if (count > 1) "${count}×${dieConfig.type.name}" else dieConfig.type.name,
                style = MaterialTheme.typography.headlineMedium,
                color = Color.White
            )

            if (!revealed) {
                Text(
                    text = rollingMessage,
                    style = MaterialTheme.typography.displaySmall,
                    color = Color.White,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.padding(horizontal = 32.dp)
                )
            } else if (totalEnabled) {
                val shown = results.orEmpty()
                val sum = shown.sum()
                val total = sum + rollModifier
                // Le total n'est coloré que s'il correspond à la valeur naturelle d'un
                // dé unique ; sinon ce sont les dés du détail qui portent la couleur.
                val totalIsSingleNaturalDie = shown.size == 1 && rollModifier == 0
                val totalColor = if (totalIsSingleNaturalDie) {
                    criticalOverlayColor(dieConfig.outcomeFor(total))
                } else {
                    Color.White
                }
                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(4.dp)
                ) {
                    Text(
                        text = total.toString(),
                        style = MaterialTheme.typography.displayLarge,
                        color = totalColor,
                        textAlign = TextAlign.Center
                    )
                    val breakdown = annotatedTotalBreakdown(
                        results = shown,
                        sum = sum,
                        modifier = rollModifier,
                        neutralColor = Color.White.copy(alpha = 0.7f),
                        resultColor = { criticalOverlayColor(dieConfig.outcomeFor(it)) }
                    )
                    if (breakdown != null) {
                        Text(
                            text = breakdown,
                            style = MaterialTheme.typography.titleMedium,
                            textAlign = TextAlign.Center,
                            modifier = Modifier.padding(horizontal = 32.dp)
                        )
                    }
                }
            } else {
                FlowRow(
                    horizontalArrangement = Arrangement.spacedBy(
                        8.dp,
                        Alignment.CenterHorizontally
                    ),
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 32.dp)
                ) {
                    val shown = results.orEmpty()
                    shown.forEachIndexed { index, result ->
                        val value = result + rollModifier
                        val color = criticalOverlayColor(dieConfig.outcomeFor(value))
                        Text(
                            text = value.toString(),
                            style = MaterialTheme.typography.displayLarge,
                            color = color,
                            textAlign = TextAlign.Center
                        )
                        if (index < shown.size - 1) {
                            Text(
                                text = " - ",
                                style = MaterialTheme.typography.displayLarge,
                                color = Color.White,
                                textAlign = TextAlign.Center
                            )
                        }
                    }
                }
            }
        }
    }
}

/** Couleurs fixes (vert réussite / rouge échec / blanc) lisibles sur le scrim sombre. */
private fun criticalOverlayColor(outcome: DiceOutcome): Color = when (outcome) {
    DiceOutcome.CRITICAL_SUCCESS -> Color(0xFF43A047)
    DiceOutcome.CRITICAL_FAILURE -> Color(0xFFE53935)
    DiceOutcome.NORMAL -> Color.White
}

/** Halo du dé 3D posé : même teinte que le résultat, rien pour une valeur neutre. */
private fun criticalHaloColor(outcome: DiceOutcome): Color? = when (outcome) {
    DiceOutcome.CRITICAL_SUCCESS, DiceOutcome.CRITICAL_FAILURE -> criticalOverlayColor(outcome)
    DiceOutcome.NORMAL -> null
}
